import {
  Button,
  ConfirmDialog,
  FlashMessage,
  Input,
  Modal,
  ReadMoreLess,
  RichTextEditor,
} from "../../common";
import React, { useEffect, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import DateIcon from "../icons/date-icon.svg";
import PinkPlus from "../icons/plus-pink.svg";
import DeleteIcon from "../icons/delete-icon.svg";
import OrganizationIcon from "../icons/organization-icon.svg";
import AwardIcon from "../icons/award-icon.svg";
import TrophyIcon from "../icons/trophy-icon.svg";
import PlusIcon from "../icons/plus-pink.svg";
import EditIcon from "../icons/edit-icon.svg";
import LinkIcon from "../icons/external-link.svg";
import CertificateIcon from "../icons/certificate-icon.svg";
import AwardIconAfter from "../icons/award-icon-after.svg";
import CertificateIconAfter from "../icons/certificate-icon-after.svg";

import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import { Grid } from "@material-ui/core";
import { rootReducersState } from "../../../reducers";
import {
  updateCandidateAccomplishmentRequest,
  updateCandidateCertificatesRequest,
} from "../../../reducers/candidate/candidate.reducer";
import { setSessionUserRequest } from "../../../reducers/auth/session.reducer";

interface AccomplishmentItem {
  award_name: string;
  award_year: string | null;
  organization_name: string;
  description: string;
}

interface Certificate {
  id?: string;
  cert_name: string;
  organization_name: string;
  issue_date: string | null; // ISO Date string like '2025-07-10'
  expiry_date: string | null;
  cert_url: string;
  cert_image: File | string;
  description: string;
}

interface Accomplishment {
  accomplishments: AccomplishmentItem[];
  certificates: Certificate[];
  tokenData?: Partial<any>;
  isPublicPage?: boolean;
}

function AccomplishmentsSection({
  accomplishments,
  certificates,
  tokenData,
  isPublicPage,
}: Accomplishment) {
  const [awardModal, setAwardModal] = useState<boolean>(false);
  const [certificateModal, setCertificateModal] = useState<boolean>(false);
  const [chooseAwardOrCertificates, setChooseAwardOrCertificates] =
    useState<boolean>(false);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [deleteConfirmType, setDeleteConfirmType] = useState<"award" | "certificate" | null>(null);
  const [localAwards, setLocalAwards] =
    useState<AccomplishmentItem[]>(accomplishments);
  const [localCertificates, setLocalCertificates] =
    useState<Certificate[]>(certificates);
  const [certPreviews, setCertPreviews] = useState<any>([]);
  const dispatch = useDispatch();
  const candidateState = useSelector(
    ({ candidate }: rootReducersState) => candidate.candidates
  );
  const candidateViewFlag = _.get(candidateState, "candidateViewFlag", "");
  const candidateLoading = _.get(candidateState, "loading", false);
  const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Award form setup
  const {
    register: awardRegister,
    handleSubmit: handleAwardSubmit,
    control: awardControl,
    reset: resetAwards,
    watch: watchAwards,
    formState: { errors: awardErrors },
  } = useForm<{ accomplishments: AccomplishmentItem[] }>({
    defaultValues: { accomplishments: accomplishments || [] },
  });

  const {
    fields: awardFields,
    append: appendAward,
    remove: removeAward,
  } = useFieldArray({
    control: awardControl,
    name: "accomplishments",
  });

  // Certificate form setup
  const {
    register: certificateRegister,
    handleSubmit: handleCertificateSubmit,
    control: certificateControl,
    reset: resetCertificates,
    watch: watchCertificates,
    setValue,
    formState: { errors: certificateErrors },
  } = useForm<{ certificates: Certificate[] }>({
    defaultValues: { certificates: certificates || [] },
  });

  const {
    fields: certificateFields,
    append: appendCertificate,
    remove: removeCertificate,
  } = useFieldArray({
    control: certificateControl,
    name: "certificates",
  });

  useEffect(() => {
    resetAwards({ accomplishments });
    resetCertificates({ certificates });
    // Keep rendered lists in sync when async profile data arrives/reloads.
    setLocalAwards(accomplishments || []);
    setLocalCertificates(certificates || []);

    // eslint-disable-next-line
  }, [accomplishments, certificates]);

  const onAwardSubmit = (data: { accomplishments: AccomplishmentItem[] }) => {
    dispatch(
      updateCandidateAccomplishmentRequest({
        accomplishments: data.accomplishments,
      })
    );
  };

  const onCertificateSubmit = (data: { certificates: Certificate[] }) => {
    const formData = new FormData();

    const cleanedCertificates = data.certificates.map((cert, index) => {
      let certImage = cert?.cert_image;
      if (certImage instanceof File) {
        formData.append(`cert_image_${index}`, certImage);
      }

      return {
        id: cert.id ?? undefined,
        cert_name: cert.cert_name,
        organization_name: cert.organization_name,
        issue_date: cert.issue_date,
        expiry_date: cert.expiry_date,
        cert_url: cert.cert_url,
        description: cert.description,
        cert_image: "",
      };
    });

    formData.append("certificates", JSON.stringify(cleanedCertificates));
    dispatch(updateCandidateCertificatesRequest(formData));
  };

  // Handle flash and modal closure
  useEffect(() => {
    if (candidateViewFlag === "accomplishment_success") {
      FlashMessage("Accomplishments updated successfully");
      const updated = watchAwards("accomplishments");
      setLocalAwards(updated);
      closeAllModals();
      dispatch(
        setSessionUserRequest({
          ...tokenData,
          accomplishments: updated,
        })
      );
    }
    if (candidateViewFlag === "accomplishment_failed") {
      FlashMessage("Failed to update accomplishments", "error");
    }

    if (candidateViewFlag === "certificate_success") {
      FlashMessage("Certificates updated successfully");
      const updated = watchCertificates("certificates");
      setLocalCertificates(updated);
      closeAllModals();
      dispatch(
        setSessionUserRequest({
          ...tokenData,
          candidateCertificates: updated,
        })
      );
    }
    if (candidateViewFlag === "certificate_failed") {
      FlashMessage("Failed to update certificates", "error");
    }

    // eslint-disable-next-line
  }, [candidateViewFlag]);

  const openAccomplishmentModal = () => {
    if (isPublicPage) return;
    setChooseAwardOrCertificates(true);
  };
  const closeAllModals = () => {
    setAwardModal(false);
    setCertificateModal(false);
    setChooseAwardOrCertificates(false);
  };

  /** Per-row removals match ProjectsSection: strip from form only; Save submits the updated list. */
  const removeCertificateAtIndex = (index: number) => {
    removeCertificate(index);
    setCertPreviews((prev: Record<number, string>) => {
      const result: Record<number, string> = {};
      Object.entries(prev || {}).forEach(([key, url]) => {
        const idx = parseInt(key, 10);
        if (idx < index) result[idx] = url as string;
        else if (idx > index) result[idx - 1] = url as string;
      });
      return result;
    });
  };

  const handleDeleteAwardClick = (index: number) => {
    removeAward(index);
  };

  const handleDeleteCertificateClick = (index: number) => {
    removeCertificateAtIndex(index);
  };

  const handleDeleteAllAwardsClick = () => {
    setDeleteConfirmType("award");
    setDeleteConfirmVisible(true);
  };

  const handleDeleteAllCertificatesClick = () => {
    setDeleteConfirmType("certificate");
    setDeleteConfirmVisible(true);
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmType === "award") {
      resetAwards({ accomplishments: [] });
      setLocalAwards([]);
      dispatch(updateCandidateAccomplishmentRequest({ accomplishments: [] }));
    } else if (deleteConfirmType === "certificate") {
      resetCertificates({ certificates: [] });
      setLocalCertificates([]);
      setCertPreviews({});
      const formData = new FormData();
      formData.append("certificates", JSON.stringify([]));
      dispatch(updateCandidateCertificatesRequest(formData));
    }
    setDeleteConfirmVisible(false);
    setDeleteConfirmType(null);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmVisible(false);
    setDeleteConfirmType(null);
  };

  const handleUploadImage = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const file = e.target.files?.[0];

    if (!file) return;

    const allowedTypes = ["image/png", "image/jpeg", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      FlashMessage("Only PNG, JPEG, and GIF files are allowed", "error");
      if (fileInputRefs.current[index]) {
        fileInputRefs.current[index]!.value = "";
      }
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      FlashMessage("File size must be less than 5 MB.", "error");
      if (fileInputRefs.current[index]) {
        fileInputRefs.current[index]!.value = "";
      }
      return;
    }

    setValue(`certificates.${index}.cert_image`, file, {
      shouldDirty: true,
      shouldValidate: true,
    });

    const url = URL.createObjectURL(file);
    setCertPreviews((prev: any) => ({
      ...prev,
      [index]: url,
    }));
  };

  useEffect(() => {
    certificates?.forEach((_, index) => {
      certificateRegister(`certificates.${index}.cert_image`);
    });
    // eslint-disable-next-line
  }, [certificateRegister, certificates]);

  return (
    <div className="accordion">
      <div className="accordion-head">
        <b>Accomplishments</b>
        {!isPublicPage &&
          (localAwards?.length || localCertificates?.length ? (
            <div className="edit-icon" onClick={openAccomplishmentModal}>
              <img src={EditIcon} alt="edit" />
            </div>
          ) : (
            <span onClick={openAccomplishmentModal}>+Add</span>
          ))}
      </div>
      {localAwards?.length || localCertificates?.length ? (
        <div className="accordion-body">
          <div
            className={`accomplishments-content${
              isPublicPage ? " main-public-page profile-timeline-rail" : ""
            }`}
          >
            {!_.isEmpty(localAwards) && (
              <div
                className={`accomplishment-inner accomplishment-inner--award${
                  isPublicPage ? " profile-timeline-item" : ""
                }`}
              >
                {isPublicPage && (
                  <span className="profile-timeline-icon" aria-hidden="true">
                    <img src={AwardIconAfter} alt="" />
                  </span>
                )}
                <h4 className="accomplishment-title">Award</h4>
                <div className="accomplishment-list-wrapper">
                  {localAwards?.map((accomplishment, index) => (
                    <div key={index} className="accomplishment-item">
                      <h5>{accomplishment.award_name}</h5>
                      <span className="organization">
                        {accomplishment.organization_name}
                      </span>
                      <div className="bordered-content wysiwyg-data">
                        <ReadMoreLess text={accomplishment.description} textLimit={300} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!_.isEmpty(localCertificates) && (
              <div
                className={`accomplishment-inner accomplishment-inner--certificate${
                  isPublicPage ? " profile-timeline-item" : ""
                }`}
              >
                {isPublicPage && (
                  <span
                    className="profile-timeline-icon profile-timeline-icon--wide"
                    aria-hidden="true"
                  >
                    <img src={CertificateIconAfter} alt="" />
                  </span>
                )}
                <h4 className="accomplishment-title">Certificate</h4>
                <div className="accomplishment-list-wrapper">
                  {localCertificates?.map((accomplishment, index) => (
                    <div key={index} className="accomplishment-item">
                      <h5>{accomplishment.cert_name}</h5>
                      <span className="organization">
                        {accomplishment.organization_name}
                      </span>
                      {!_.isEmpty(accomplishment?.cert_image) ? (
                        <div className="certificate-img">
                          <img
                            src={accomplishment?.cert_image as string}
                            alt="certificate_image"
                          />
                        </div>
                      ) : certPreviews?.[index] ? (
                        <div className="certificate-img">
                          <img
                            src={certPreviews?.[index] as string}
                            alt="certificate_image"
                            style={{ objectFit: "cover" }}
                          />
                        </div>
                      ) : null}
                      <div className="certificate-url">
                        {accomplishment?.cert_url}
                      </div>
                      <div className="bordered-content wysiwyg-data">
                        <ReadMoreLess text={accomplishment.description} textLimit={300} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}
      {!isPublicPage && (
        <Modal
          className="candidate-profile-modal accomplishments-modal"
          isOpen={chooseAwardOrCertificates}
          onClose={() => setChooseAwardOrCertificates(false)}
          title="Accomplishments"
          visible={chooseAwardOrCertificates}
          size="large"
        >
          {!_.isEmpty(localAwards) || !_.isEmpty(localCertificates) ? (
            <div className="modal-body-wrapper award-update-modal">
              <div className="award-wrapper">
                <div className="award-content">
                  <div className="award-row">
                    <div className="icon">
                      <img src={TrophyIcon} alt="icon" />
                    </div>
                    <span>Award</span>
                  </div>
                  <div className="action">
                    <div className="icon-wrapper">
                      <div
                        className="action-icon"
                        onClick={() => setAwardModal(true)}
                      >
                        <img src={EditIcon} alt="edit" />
                      </div>
                      {
                        localAwards?.length > 0 && (
                          <div
                            className="action-icon"
                            onClick={handleDeleteAllAwardsClick}
                          >
                            <img src={DeleteIcon} alt="delete" />
                          </div>
                        )
                      }
                    </div>
                  </div>
                </div>
                <div className="award-content">
                  <div className="award-row">
                    <div className="icon">
                      <img src={CertificateIcon} alt="icon" />
                    </div>
                    <span>Certificate</span>
                  </div>
                  <div className="action">
                    <div className="icon-wrapper">
                      <div
                        className="action-icon"
                        onClick={() => setCertificateModal(true)}
                      >
                        <img src={EditIcon} alt="edit" />
                      </div>
                      {
                        localCertificates?.length > 0 && (
                          <div
                            className="action-icon"
                            onClick={handleDeleteAllCertificatesClick}
                          >
                            <img src={DeleteIcon} alt="delete" />
                          </div>
                        )
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="modal-body-wrapper award-update-modal">
              <p>
                Showcase your proudest recognitions and completed certifications
                to strengthen your profile.
              </p>
              <div className="choose-award-row-wrapper">
                <div className="award-row" onClick={() => setAwardModal(true)}>
                  <div className="icon">
                    <img src={PlusIcon} alt="icon" />
                  </div>
                  <span>Add award</span>
                </div>
                <div
                  className="award-row"
                  onClick={() => setCertificateModal(true)}
                >
                  <div className="icon">
                    <img src={PlusIcon} alt="icon" />
                  </div>
                  <span>Add Certificate</span>
                </div>
              </div>
            </div>
          )}
        </Modal>
      )}
      <Modal
        className="candidate-profile-modal project-modal"
        isOpen={awardModal}
        onClose={() => setAwardModal(false)}
        title="Award"
        visible={awardModal}
        size="large"
      >
        <div className="modal-body-wrapper">
          <form onSubmit={handleAwardSubmit(onAwardSubmit)}>
            {!_.isEmpty(awardFields) ? (
              awardFields?.map((item, index) => (
                <div className="form-wrapper" key={index}>
                  <div className="form-group">
                    <Input
                      inputIcon={
                        <div className="input-icon">
                          <img src={AwardIcon} alt="icon" />
                        </div>
                      }
                      className="input-with-icon"
                      defaultValue={item.award_name}
                      externalLabel={{ label: "Award Name", className: "required" }}
                      placeholder="Award Name"
                      validationObj={awardErrors}
                      {...awardRegister(`accomplishments.${index}.award_name`, {
                        required: {
                          value: true,
                          message: "Please enter the Award Name.",
                        },
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <Input
                      inputIcon={
                        <div className="input-icon">
                          <img src={OrganizationIcon} alt="icon" />
                        </div>
                      }
                      className="input-with-icon"
                      defaultValue={item?.organization_name}
                      externalLabel={{ label: "Organization Name", className: "required" }}
                      placeholder="Organization Name"
                      validationObj={awardErrors}
                      {...awardRegister(
                        `accomplishments.${index}.organization_name`,
                        {
                          required: {
                            value: true,
                            message: "Please enter the Organization Name.",
                          },
                        }
                      )}
                    />
                  </div>
                  <div className="form-group">
                    <Input
                      inputIcon={
                        <div className="input-icon">
                          <img src={DateIcon} alt="icon" />
                        </div>
                      }
                      type="date"
                      className="input-with-icon"
                      defaultValue={item?.award_year}
                      externalLabel={{ label: "Award date", className: "required" }}
                      placeholder="Award date"
                      validationObj={awardErrors}
                      inputProps={{
                        max: new Date().toISOString().split('T')[0]
                      }}
                      {...awardRegister(`accomplishments.${index}.award_year`, {
                        required: {
                          value: true,
                          message: "Please enter the Award date.",
                        },
                        validate: (value) => {
                          if (!value) return true; // Allow empty if not required (though it is required)
                          const today = new Date().toISOString().split('T')[0];
                          if (value > today) {
                            return "Award date cannot be in the future";
                          }
                          return true;
                        },
                      })}
                    />
                  </div>

                  <div className="form-group ">
                    <RichTextEditor
                      name={`accomplishments.${index}.description`}
                      defaultValue={item?.description}
                      externalLabel={{
                        label: "Description",
                        className: "required",
                      }}
                      placeholder="Description"
                      validationObj={awardErrors}
                      control={awardControl}
                      rules={{
                        required: {
                          value: true,
                          message: "Please enter award description",
                        },
                      }}
                      // showCount
                      // maxLimit={maxLimit}
                      counterAlign="right"
                    />
                    <div className="action-wrapper">
                      <div
                        className="icon"
                        onClick={() =>
                          appendAward({
                            award_name: "",
                            organization_name: "",
                            award_year: "",
                            description: "",
                          })
                        }
                      >
                        <img src={PinkPlus} alt="edit" />
                      </div>
                      <div className="icon" onClick={() => handleDeleteAwardClick(index)}>
                        <img src={DeleteIcon} alt="Delete" />
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <Button
                type="button"
                className="add-new-btn"
                onClick={() =>
                  appendAward({
                    award_name: "",
                    organization_name: "",
                    award_year: "",
                    description: "",
                  })
                }
              >
                Add new
              </Button>
            )}
            <div className="btn-wrapper">
              <Button
                variant="outlined"
                className="outlined"
                onClick={() => {
                  setAwardModal(false);
                }}
                color="none"
              >
                Cancel
              </Button>
              <Button
                className="save-btn"
                type="submit"
                loading={candidateLoading && awardModal}
              >
                Save
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      <Modal
        className="candidate-profile-modal project-modal certificate-modal"
        isOpen={certificateModal}
        onClose={() => setCertificateModal(false)}
        title="Certificate"
        visible={certificateModal}
        size="large"
      >
        <div className="modal-body-wrapper">
          <form onSubmit={handleCertificateSubmit(onCertificateSubmit)}>
            {!_.isEmpty(certificateFields) ? (
              certificateFields.map((item, index) => (
                <div className="form-wrapper" key={index}>
                  <Input
                    className="!d-none"
                    style={{ display: "none" }}
                    defaultValue={item.id}
                    {...certificateRegister(`certificates.${index}.id`, {
                      required: {
                        value: false,
                        message: "Please enter the certificate name.",
                      },
                    })}
                  />
                  {/* Certificate Name */}
                  <div className="form-group">
                    <Input
                      inputIcon={
                        <div className="input-icon">
                          <img src={AwardIcon} alt="icon" />
                        </div>
                      }
                      className="input-with-icon"
                      defaultValue={item.cert_name}
                      externalLabel={{ label: "Certificate Name", className: "required" }}
                      placeholder="Certificate Name"
                      validationObj={certificateErrors}
                      {...certificateRegister(
                        `certificates.${index}.cert_name`,
                        {
                          required: {
                            value: true,
                            message: "Please enter the certificate name.",
                          },
                        }
                      )}
                    />
                  </div>

                  {/* Organization Name */}
                  <div className="form-group">
                    <Input
                      inputIcon={
                        <div className="input-icon">
                          <img src={OrganizationIcon} alt="icon" />
                        </div>
                      }
                      className="input-with-icon"
                      defaultValue={item.organization_name}
                      externalLabel={{ label: "Organization Name", className: "required" }}
                      placeholder="Organization Name"
                      validationObj={certificateErrors}
                      {...certificateRegister(
                        `certificates.${index}.organization_name`,
                        {
                          required: {
                            value: true,
                            message: "Please enter the organization name.",
                          },
                        }
                      )}
                    />
                  </div>

                  {/* Issue & Expiry Dates */}
                  <Grid container spacing={2}>
                    <Grid item sm={6} xs={12}>
                      <div className="form-group">
                        <Input
                          inputIcon={
                            <div className="input-icon">
                              <img src={DateIcon} alt="icon" />
                            </div>
                          }
                          type="date"
                          className="input-with-icon"
                          defaultValue={item.issue_date}
                          externalLabel={{ label: "Issued Date", className: "required" }}
                          placeholder="Issued Date"
                          validationObj={certificateErrors}
                          inputProps={{
                            max: new Date().toISOString().split('T')[0]
                          }}
                          {...certificateRegister(
                            `certificates.${index}.issue_date`,
                            {
                              required: {
                                value: true,
                                message: "Please enter the issue date.",
                              },
                              validate: (value) => {
                                if (!value) return true;
                                const today = new Date().toISOString().split('T')[0];
                                if (value > today) {
                                  return "Issue date cannot be in the future";
                                }
                                return true;
                              },
                            }
                          )}
                        />
                      </div>
                    </Grid>
                    <Grid item sm={6} xs={12}>
                      {/* Certificate URL */}
                      <div className="form-group">
                        <Input
                          inputIcon={
                            <div className="input-icon">
                              <img src={LinkIcon} alt="icon" />
                            </div>
                          }
                          className="input-with-icon"
                          defaultValue={item.cert_url}
                          externalLabel={{ label: "Certificate URL", className: "required" }}
                          placeholder="Certificate URL"
                          validationObj={certificateErrors}
                          {...certificateRegister(
                            `certificates.${index}.cert_url`,
                            {
                              required: {
                                value: true,
                                message: "Please enter the certificate URL.",
                              },
                              validate: (value) => {
                                const urlValue = String(value || "").trim();
                                if (!urlValue) {
                                  return "Please enter the certificate URL.";
                                }
                                const urlRegex = /^(https?:\/\/)((([a-z\d]([a-z\d-]*[a-z\d])*)\.)+[a-z]{2,}|((\d{1,3}\.){3}\d{1,3}))(:\d+)?(\/[-a-z\d%_.~+]*)*(\?[;&a-z\d%_.~+=-]*)?(#[-a-z\d_]*)?$/i;
                                if (!urlRegex.test(urlValue)) {
                                  return "Please enter a valid URL.";
                                }
                                return true;
                              },
                            }
                          )}
                        />
                      </div>
                    </Grid>
                  </Grid>



                  {/* Certificate Image */}
                  <div className="form-group">
                    <div
                      className="add-image"
                      onClick={() => fileInputRefs.current[index]?.click()}
                    >
                      <Button
                        variant="outlined"
                        className="outlined"
                        color="none"
                      >
                        <div className="icon">
                          <img src={PlusIcon} alt="Add" />
                        </div>
                        <span>Add Image</span>
                        <input
                          type="file"
                          accept="image/png, image/jpeg, image/gif"
                          ref={(el: any) => (fileInputRefs.current[index] = el)}
                          style={{ display: "none" }}
                          onChange={(e: any) => handleUploadImage(e, index)}
                        />
                      </Button>
                    </div>
                  </div>
                  {certPreviews[index] ? (
                    <div className="form-control">
                      <img
                        src={(certPreviews[index] as any) || ""}
                        alt="certificate"
                        style={{ maxHeight: "150px", marginTop: "10px" }}
                      />
                    </div>
                  ) : item?.cert_image ? (
                    <div className="form-control">
                      <img
                        src={(item?.cert_image as any) || ""}
                        alt="certificate"
                        style={{ maxHeight: "150px", marginTop: "10px" }}
                      />
                    </div>
                  ) : null}
                  {/* Description + Add/Remove buttons */}
                  <div className="form-group">
                    <RichTextEditor
                      name={`certificates[${index}].description`}
                      defaultValue={item.description}
                      externalLabel={{
                        label: "Description",
                        className: "required",
                      }}
                      placeholder="Description"
                      validationObj={certificateErrors}
                      control={certificateControl}
                      rules={{
                        required: {
                          value: true,
                          message: "Please enter certificate description",
                        },
                      }}
                      counterAlign="right"
                    />
                    <div className="action-wrapper">
                      <div
                        className="icon"
                        onClick={() =>
                          appendCertificate({
                            cert_name: "",
                            organization_name: "",
                            issue_date: null,
                            expiry_date: null,
                            cert_url: "",
                            cert_image: "",
                            description: "",
                            id: "",
                          })
                        }
                      >
                        <img src={PinkPlus} alt="Add" />
                      </div>
                      <div
                        className="icon"
                        onClick={() => handleDeleteCertificateClick(index)}
                      >
                        <img src={DeleteIcon} alt="Delete" />
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <Button
                type="button"
                className="add-new-btn"
                onClick={() =>
                  appendCertificate({
                    cert_name: "",
                    organization_name: "",
                    issue_date: null,
                    expiry_date: null,
                    cert_url: "",
                    cert_image: "",
                    description: "",
                    id: "",
                  })
                }
              >
                Add new
              </Button>
            )}

            {/* Submit Buttons */}
            <div className="btn-wrapper">
              <Button
                variant="outlined"
                className="outlined"
                onClick={() => setCertificateModal(false)}
                color="none"
              >
                Cancel
              </Button>
              <Button
                className="save-btn"
                type="submit"
                loading={candidateLoading && certificateModal}
              >
                Save
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      <ConfirmDialog
        visible={deleteConfirmVisible}
        title="Delete Confirmation"
        bodyText={
          deleteConfirmType === "award"
            ? "Are you sure you want to delete all awards?"
            : "Are you sure you want to delete all certificates?"
        }
        confirmText="Delete"
        loading={candidateLoading}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
}

export default AccomplishmentsSection;
